import { Grid } from '@material-ui/core'
import React from 'react'
import CreditCardIcon from '@mui/icons-material/CreditCard'
import { Button, Divider, Typography } from '@mui/material'
import Box from '@mui/material/Box'
import { isEmpty } from '../../../../utilities/generalUtils'
import GuestChargingHistory from '../../Components/GuestChargingHistory'
import EditBilling from '../../Components/EditBilling'
import QRChargeComponent from '../../Components/QRCharge'
import { usePropertyCost } from '../../../../hooks/usePropertyCost'
import { useUserAccess } from '../../../../hooks/useUserAccess'
import { useGuestSession } from './useGuestSession'
import ActiveSessionCard from './ActiveSessionCard'
import { useAuth } from '../../../../auth/Auth'

const ConsumerGuestView = ({
    data,
    reloadWallet,
    userWalletData,
    myProperty,
    showManageBilling,
    manageBilling,
    setShowManageBilling,
    stripePromise,
}) => {
    const auth = useAuth()
    const attrs = auth.user?.attributes || {}
    const firstName =
        attrs.given_name ||
        (attrs.name ? attrs.name.trim().split(/\s+/)[0] : '') ||
        ''

    const { costData, loading: costLoading, error: costError } = usePropertyCost(myProperty?.uuid);

    const {
        canAccessSharedCharging,
        canUseSharedCharger,
        isGuestAccessExpired,
        getCreditCardStatus,
    } = useUserAccess();

    const {
        myVehicle,
        activeSession,
        showConfirmStop,
        sessionStoppedAwaitingComplete,
        sessionCompletedDisplay,
        stoppedSessionData,
        stoppedSessionUuid,
        handleEndChargeClick,
        handleConfirmStopCharge,
        clearStopDisplayState,
        setShowConfirmStop,
    } = useGuestSession(data);

    const showActiveSession = (
        (!isEmpty(activeSession) && activeSession?.session?.uuid !== stoppedSessionUuid)
        || sessionStoppedAwaitingComplete
        || sessionCompletedDisplay
    );

    const showStartCharge = (
        (isEmpty(activeSession) || activeSession?.session?.uuid === stoppedSessionUuid)
        && !sessionStoppedAwaitingComplete
        && !sessionCompletedDisplay
    );

    function startChargeContent() {
        const creditCardStatus = getCreditCardStatus();

        if (creditCardStatus === 'no_card') {
            return (
                <Typography variant='body1' className='start-charge-message'>
                    You need to add a credit card before charging.
                    <br />Please add a payment method in Manage Billing.
                </Typography>
            );
        }

        if (creditCardStatus === 'invalid') {
            return (
                <Typography variant='body1' className='start-charge-message'>
                    There is an issue with your credit card.
                    <br />Please fix your payment method in Manage Billing.
                </Typography>
            );
        }

        if (isGuestAccessExpired()) {
            return (
                <Typography variant='body1' className='start-charge-message'>
                    Your guest charging access is expired.
                    <br />Please request a new guest link from a resident.
                </Typography>
            );
        }

        if (!canAccessSharedCharging()) {
            return (
                <Typography variant='body1' className='start-charge-message'>
                    You dont have access to guest charging.
                    <br />Please request a guest link from a resident.
                </Typography>
            );
        }

        return (
            <QRChargeComponent
                userVehicle={myVehicle}
                guestChargers={data?.guestChargers}
                canUseSharedCharger={canUseSharedCharger}
                smartOutlets={data?.smartOutlets}
                costData={costData}
                costLoading={costLoading}
                costError={costError}
                powerType={myProperty?.powerType}
                buttonText='Start Charge'
                buttonClassName='start-charge'
            />
        );
    }

    return (
        <div className='dashboard-page-container'>
            <h1 className='gp-page-title'>
                Welcome back{firstName ? `, ${firstName}` : ''}
            </h1>
            <p className='gp-page-sub'>
                Here's a quick look at your charging, wallet and property.
            </p>
            <Grid container>
                <Grid item xs={12} md={12}>
                    <div className='dashboard-page-container-full'>
                        {!showManageBilling && (
                            <Grid item xs={12} md={6} lg={3}>
                                <div className='dash-section-title'>Guest Charging</div>
                                {showStartCharge && (
                                    <>
                                        <Box sx={{ height: 8, mt: 0, mb: 0 }} />
                                        {startChargeContent()}
                                    </>
                                )}
                                {showActiveSession && (
                                    <ActiveSessionCard
                                        activeSession={activeSession}
                                        stoppedSessionData={stoppedSessionData}
                                        sessionStoppedAwaitingComplete={sessionStoppedAwaitingComplete}
                                        sessionCompletedDisplay={sessionCompletedDisplay}
                                        showConfirmStop={showConfirmStop}
                                        myProperty={myProperty}
                                        onStopClick={handleEndChargeClick}
                                        onConfirmStop={handleConfirmStopCharge}
                                        onCancelStop={() => setShowConfirmStop(false)}
                                        onDismiss={clearStopDisplayState}
                                    />
                                )}
                                <Divider sx={{ my: 2, borderBottomWidth: 2 }} />
                                <Button
                                    className='consumer-buttons'
                                    variant='contained'
                                    fullWidth
                                    startIcon={<CreditCardIcon fontSize='small' />}
                                    onClick={manageBilling}
                                >
                                    Manage Billing
                                </Button>
                                <GuestChargingHistory />
                            </Grid>
                        )}
                        {showManageBilling && (
                            <EditBilling
                                data={data}
                                reloadWallet={reloadWallet}
                                setShowManageBilling={setShowManageBilling}
                                stripePromise={stripePromise}
                                userWalletData={userWalletData}
                                myProperty={myProperty}
                            />
                        )}
                    </div>
                </Grid>
            </Grid>
        </div>
    );
};

export default ConsumerGuestView;
